import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
} from "react";

type Side = "left" | "right";

interface HoverRevealProps {
  children: ReactNode;
  panelContent: ReactNode;
  side?: Side;
  hideDelay?: number;
}

export const HoverReveal: React.FC<HoverRevealProps> = ({
  children,
  panelContent,
  side = "right",
  hideDelay = 200,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (): void => {
    clearHideTimeout();
    setVisible(true);
  };

  const handleMouseLeave = (): void => {
    clearHideTimeout();

    hideTimeoutRef.current = setTimeout(() => {
      setVisible(false);
    }, hideDelay);
  };

  const handleClick = (): void => {
    setVisible((prev) => !prev);
  };

  const panelPosition: React.CSSProperties =
    side === "left"
      ? { right: "100%", marginRight: "0px" }
      : { left: "100%", marginLeft: "0px" };

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      className="w-full relative inline-block"
    >
        {/* main element */}
        <div className="w-full">{children}</div>

        {/* content */}
        {visible && (
            <div
                style={{
                    ...panelPosition,
                }}
                className="px-2 pb-2 z-50 top-0 absolute whitespace-nowrap"
            >
                {panelContent}
            </div>
        )}
    </div>
  );
};
